import { useMutation, useQuery, useQueryClient } from "react-query";
import * as api from "../api/grantApproval";

export function useGetPendingSignOffs() {
  return useQuery(["pendingSignOffs"], api.getPendingSignOffs, {
    staleTime: 1000 * 60 * 5,
  });
}

export function useGetGrantApprovalStatus(grantId: string) {
  return useQuery(
    ["grantApprovalStatus", grantId],
    () => api.getGrantApprovalStatus(grantId),
    {
      enabled: !!grantId,
      staleTime: 1000 * 60 * 2,
    }
  );
}

export function useSubmitSignOff() {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      signOffId,
      payload,
    }: {
      signOffId: string;
      payload: api.SignOffPayload;
    }) => api.submitSignOff(signOffId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["pendingSignOffs"]);
        queryClient.invalidateQueries(["grantApprovalStatus"]);
        queryClient.invalidateQueries("allGrants");
      },
    }
  );
}

export function useSubmitBulkApproverActions() {
  const queryClient = useQueryClient();

  return useMutation(api.submitBulkApproverActions, {
    onSuccess: () => {
      queryClient.invalidateQueries(["pendingSignOffs"]);
      queryClient.invalidateQueries(["grantApprovalStatus"]);
      queryClient.invalidateQueries("managerTeam");
      queryClient.invalidateQueries("managerDashboard");
      queryClient.invalidateQueries("allGrants");
    },
  });
}

export function useParseBulkRecommendationUpload() {
  return useMutation(
    ({
      file,
      grants,
    }: {
      file: string;
      grants: { id: string; identifier: string }[];
    }) => api.parseBulkRecommendationUpload(file, grants)
  );
}

export function useDownloadBulkRecommendationTemplate() {
  return useMutation(api.downloadBulkRecommendationTemplate);
}

export function useOverrideSignOff() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ signOffId, comment }: { signOffId: string; comment?: string }) =>
      api.overrideSignOff(signOffId, comment),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["grantApprovalStatus"]);
        queryClient.invalidateQueries(["pendingSignOffs"]);
        queryClient.invalidateQueries("allGrants");
      },
    }
  );
}

export function useResetApprovalFlow() {
  const queryClient = useQueryClient();

  return useMutation((grantId: string) => api.resetApprovalFlow(grantId), {
    onSuccess: () => {
      queryClient.invalidateQueries(["grantApprovalStatus"]);
      queryClient.invalidateQueries(["pendingSignOffs"]);
      queryClient.invalidateQueries("allGrants");
    },
  });
}

export function useSubmitRecommendChange() {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      signOffId,
      payload,
    }: {
      signOffId: string;
      payload: api.RecommendChangePayload;
    }) => api.submitRecommendChange(signOffId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["pendingSignOffs"]);
        queryClient.invalidateQueries(["grantApprovalStatus"]);
        queryClient.invalidateQueries(["grantsWithRecommendedChanges"]);
      },
    }
  );
}

export function useGetGrantsWithRecommendedChanges() {
  return useQuery(
    ["grantsWithRecommendedChanges"],
    () => api.getGrantsWithRecommendedChanges(),
    {
      staleTime: 1000 * 60 * 5,
    }
  );
}

// Fetches the open recommendation records for a single grant — lazy, only
// meaningful once a specific grant is being reviewed.
export function useGetGrantRecommendations(grantId: string) {
  return useQuery(
    ["grantsWithRecommendedChanges", grantId],
    () => api.getGrantsWithRecommendedChanges(grantId),
    {
      enabled: !!grantId,
      staleTime: 1000 * 60 * 2,
    }
  );
}

export function useAcceptRecommendedChanges() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ grantId, signOffId }: { grantId: string; signOffId: string }) =>
      api.acceptRecommendedChanges(grantId, signOffId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["grantsWithRecommendedChanges"]);
        queryClient.invalidateQueries(["grantApprovalStatus"]);
        queryClient.invalidateQueries("allGrants");
      },
    }
  );
}

export function useRejectRecommendedChanges() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ grantId, signOffId }: { grantId: string; signOffId: string }) =>
      api.rejectRecommendedChanges(grantId, signOffId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["grantsWithRecommendedChanges"]);
        queryClient.invalidateQueries(["grantApprovalStatus"]);
      },
    }
  );
}
